import styled from "styled-components";
import { colors } from "src/styles";

export const FormContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px;
  background-color: ${colors.white.default};
  border-radius: 8px;
  box-shadow: 0px 4px 120px rgba(0, 0, 0, 0.1);
`;

export const SectionContainer = styled.div`
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid ${colors.grey.light12};

  &:last-child {
    border-bottom: none;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.black.light2};
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const InputRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

export const InputFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 200px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.black.light2};
    margin-bottom: 8px;
  }

  input,
  textarea,
  select {
    padding: 10px 12px;
    border: 1px solid ${colors.grey.light12};
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: ${colors.blue.default};
      box-shadow: 0 0 0 2px rgba(41, 128, 185, 0.1);
    }

    &:disabled {
      background-color: ${colors.grey.light13};
      color: ${colors.grey.dark};
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

export const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    color: ${colors.black.light2};
    cursor: pointer;
    margin: 0;
  }
`;

export const RadioButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
`;

export const RadioButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    color: ${colors.black.light2};
    cursor: pointer;
    margin: 0;
  }
`;

export const AccordionContainer = styled.div`
  margin-bottom: 20px;

  .accordion {
    border: 1px solid ${colors.grey.light12};
    border-radius: 4px;
    overflow: hidden;

    .accordion-item {
      border-bottom: 1px solid ${colors.grey.light12};

      &:last-child {
        border-bottom: none;
      }
    }

    .accordion-button {
      padding: 15px 20px;
      font-weight: 600;
      font-size: 14px;
      color: ${colors.black.light2};
      background-color: ${colors.grey.light14};
      border: none;

      &:not(.collapsed) {
        background-color: ${colors.grey.light13};
        box-shadow: none;
        color: ${colors.black.light2};
      }

      &:focus {
        border-color: ${colors.grey.light12};
        box-shadow: none;
      }

      &::after {
        background-image: none;
      }
    }

    .accordion-body {
      padding: 20px;
      background-color: ${colors.white.default};
    }
  }
`;

export const TableContainer = styled.div`
  overflow-x: auto;
  margin-bottom: 20px;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${colors.grey.light12};

  thead {
    background-color: ${colors.grey.light14};
    border-bottom: 2px solid ${colors.grey.light12};
  }

  th {
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    color: ${colors.black.light2};
    white-space: nowrap;
  }

  td {
    padding: 12px 15px;
    border-bottom: 1px solid ${colors.grey.light12};
    font-size: 14px;
  }

  tbody tr:hover {
    background-color: ${colors.grey.light14};
  }

  input {
    width: 100%;
    padding: 8px;
    border: 1px solid ${colors.grey.light12};
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: ${colors.blue.default};
    }
  }
`;

export const TotalRow = styled.tr`
  background-color: ${colors.grey.light14};
  font-weight: 600;

  td {
    border-top: 2px solid ${colors.grey.dark};
    border-bottom: 2px solid ${colors.grey.dark};
    padding: 15px;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const AddRowButton = styled.button`
  padding: 8px 16px;
  background-color: ${colors.blue.default};
  color: ${colors.white.default};
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1a6bc9;
  }

  &:disabled {
    background-color: ${colors.grey.dark};
    cursor: not-allowed;
  }
`;

export const RemoveRowButton = styled.button`
  padding: 6px 12px;
  background-color: ${colors.red.default};
  color: ${colors.white.default};
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #c0392b;
  }

  &:disabled {
    background-color: ${colors.grey.dark};
    cursor: not-allowed;
  }
`;

export const TotalAmountDisplay = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  background-color: ${colors.grey.light14};
  border-radius: 4px;
  margin-top: 15px;

  p {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.black.light2};

    span {
      margin-left: 20px;
      font-size: 18px;
      color: ${colors.blue.default};
    }
  }
`;

export const QuoteTotalContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  background-color: #f0f7ff;
  border-radius: 4px;
  margin: 30px 0;

  p {
    font-size: 18px;
    font-weight: 700;
    color: ${colors.blue.default};

    span {
      margin-left: 30px;
      font-size: 24px;
      color: ${colors.blue.default};
    }
  }
`;

export const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  input[type="file"] {
    padding: 8px;
    border: 1px solid ${colors.grey.light12};
    border-radius: 4px;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: ${colors.blue.default};
    }
  }

  .file-name {
    font-size: 12px;
    color: ${colors.grey.dark};
    padding: 5px 0;
  }
`;

export const ErrorText = styled.span`
  color: ${colors.red.default};
  font-size: 12px;
  margin-top: 4px;
`;

export const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 40px;

  button {
    padding: 12px 30px;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &.submit {
      background-color: ${colors.blue.default};
      color: ${colors.white.default};

      &:hover {
        background-color: #1a6bc9;
      }

      &:disabled {
        background-color: ${colors.grey.dark};
      }
    }

    &.cancel {
      background-color: ${colors.grey.light13};
      color: ${colors.black.light2};
      border: 1px solid ${colors.grey.light12};

      &:hover {
        background-color: ${colors.grey.light12};
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;
